/**
 * Where drawn edges cross each other (#128).
 *
 * A crossing is one of the most useful points on a drawing and one of the few
 * the snap system couldn't offer: it belongs to no path's anchor list, so
 * neither the vertex tier (which walks segments) nor the line tier (which
 * projects onto the nearest edge) can name it. Two guide lines laid out to
 * locate a rivet meet at a point that, until now, you had to eyeball.
 *
 * Deliberately a SNAP TARGET and nothing more, per the ticket: no constraint is
 * recorded, so a drawn point lands on the crossing and then stays where it
 * landed — moving one of the lines afterwards does not drag it along. That's
 * the same contract as the grid.
 *
 * The pairwise scan is O(n²) in edge count, so it is bounds-culled first: two
 * paths whose bounding boxes don't overlap cannot cross, and that check is a
 * few comparisons against a full curve-intersection solve. Callers gather once
 * per gesture, not per frame.
 */
import type paper from "paper";
import type { Vec2 } from "./Project";

/** Parsed edge plus its bounds, so the cull doesn't re-measure per pair. */
interface EdgeItem {
  path: paper.PathItem;
  bounds: paper.Rectangle;
}

/**
 * Every point where two of `edges` cross.
 *
 * `edges` are SVG path-data strings in one coordinate space; unparseable ones
 * are skipped. Self-intersections are NOT included — a path crossing itself is
 * usually an artefact of a hand-drawn stroke rather than a place the user means
 * to aim at, and including them would speckle every freehand trace.
 *
 * Results are deduped to 1e-3 mm, so three lines through one point offer that
 * point once rather than three times.
 */
export function edgeIntersections(
  edges: ReadonlyArray<string>,
  scope: paper.PaperScope,
): Vec2[] {
  const items: EdgeItem[] = [];
  for (const pathData of edges) {
    if (!pathData) continue;
    try {
      const path = new scope.CompoundPath({ pathData, insert: false });
      // A path with no length (a stray "M x y") can't cross anything.
      if (path.bounds) items.push({ path, bounds: path.bounds });
    } catch {
      /* unparseable — skip, same as every other gatherer */
    }
  }

  const out: Vec2[] = [];
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i]!;
      const b = items[j]!;
      // Bounds cull: non-overlapping boxes can't produce a crossing, and this
      // costs a few comparisons against a full curve-intersection solve.
      if (!a.bounds.intersects(b.bounds)) continue;
      let hits: paper.CurveLocation[];
      try {
        hits = a.path.getIntersections(b.path);
      } catch {
        continue;
      }
      for (const loc of hits) out.push({ x: loc.point.x, y: loc.point.y });
    }
  }
  for (const item of items) item.path.remove();
  return dedupe(out);
}

/** Drop points that coincide to within 1e-3 mm — three lines meeting at one
 *  spot should offer that spot once. */
function dedupe(pts: Vec2[]): Vec2[] {
  const seen = new Set<string>();
  const out: Vec2[] = [];
  for (const p of pts) {
    const key = `${Math.round(p.x * 1000)},${Math.round(p.y * 1000)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/** The nearest crossing to `p` within `radius`, or null. */
export function nearestIntersection(
  p: Vec2,
  intersections: ReadonlyArray<Vec2>,
  radius: number,
): Vec2 | null {
  const r2 = radius * radius;
  let best: Vec2 | null = null;
  let bestD2 = Infinity;
  for (const c of intersections) {
    const d2 = (c.x - p.x) ** 2 + (c.y - p.y) ** 2;
    if (d2 <= r2 && d2 < bestD2) {
      bestD2 = d2;
      best = c;
    }
  }
  return best ? { x: best.x, y: best.y } : null;
}
